import { Injectable, computed, inject, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  CurrentPatientScope,
  OrganisationService,
  StateBasedNavigationService,
} from '@principle-theorem/ng-principle-shared';
import {
  BasicDialogService,
  DialogPresets,
  IBreadcrumb,
  confirmationDialogData,
} from '@principle-theorem/ng-shared';
import {
  Brand,
  InitReferralData,
  Referral,
} from '@principle-theorem/principle-core';
import {
  IBrand,
  IPatient,
  IReferral,
  MentionResourceType,
  ReferralActor,
  ReferralStatus,
  StafferActor,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  Firestore,
  WithRef,
  isSameIdentifier,
  snapshot,
  snapshotDefined,
  toNamedTypeDocument,
  updateDoc,
} from '@principle-theorem/shared';
import {
  IReferralCreateDialogData,
  ReferralCreateDialogComponent,
} from '../components/referral-create-dialog/referral-create-dialog.component';
import { ReferralInteractionService } from './referral-interaction.service';
import { Observable, combineLatest } from 'rxjs';
import { switchMap } from 'rxjs/internal/operators/switchMap';
import { map } from 'rxjs/operators';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class ReferralService {
  private _organisation = inject(OrganisationService);
  private _patientScope = inject(CurrentPatientScope);
  private _stateNav = inject(StateBasedNavigationService);
  private _router = inject(Router);
  private _dialog = inject(MatDialog);
  private _basicDialog = inject(BasicDialogService);
  private _snackBar = inject(MatSnackBar);
  private _interactionService = inject(ReferralInteractionService);

  private _isSaving = signal(false);
  public isSaving = computed(() => this._isSaving());

  getReferrals$(
    brand$: Observable<WithRef<IBrand>>,
    patient$: Observable<WithRef<IPatient> | undefined>
  ): Observable<WithRef<IReferral>[]> {
    return combineLatest([brand$, patient$]).pipe(
      switchMap(([brand, patient]) =>
        patient ? Referral.byPatient$(brand, patient) : Referral.all$(brand)
      )
    );
  }

  getBreadcrumbs$(
    patient$: Observable<WithRef<IPatient> | undefined>
  ): Observable<IBreadcrumb[]> {
    return patient$.pipe(
      map((patient) => (patient ? [] : [{ label: 'Referrals' }]))
    );
  }

  async openCreateDialog(
    openInNewTab = false
  ): Promise<DocumentReference<IReferral> | undefined> {
    const brand = await snapshotDefined(this._organisation.brand$);
    const practice = await snapshotDefined(this._organisation.practice$);
    const staffer = await snapshotDefined(this._organisation.staffer$);
    const patient = await snapshot(this._patientScope.doc$);

    if (!brand || !practice || !staffer) {
      return;
    }

    const referralData = await this._dialog
      .open<
        ReferralCreateDialogComponent,
        IReferralCreateDialogData,
        InitReferralData
      >(
        ReferralCreateDialogComponent,
        DialogPresets.large({
          data: { brand, staffer, practice, patient },
        })
      )
      .afterClosed()
      .toPromise();

    if (!referralData) {
      return;
    }

    const doc = await this.create(
      brand,
      referralData,
      toNamedTypeDocument(staffer, MentionResourceType.Staffer)
    );

    if (!doc) {
      return;
    }

    const referral = await Firestore.getDoc(doc);
    await this.navigateToReferral(referral, patient, openInNewTab);
  }

  async navigateToReferral(
    referral: WithRef<IReferral>,
    patient?: WithRef<IPatient>,
    openInNewTab?: boolean
  ): Promise<void> {
    const path = patient
      ? ['patients', patient.ref.id, 'referrals', referral.ref.id]
      : ['referrals', referral.ref.id];

    if (openInNewTab) {
      const route = await snapshot(this._stateNav.link.brand$(path));
      const url = this._router.serializeUrl(this._router.createUrlTree(route));
      window.open(url, '_blank');
      return;
    }

    await this._stateNav.brand(path);
  }

  async navigateToPatient(
    patientId: string,
    openInNewTab?: boolean
  ): Promise<void> {
    const path = ['patients', patientId];

    if (openInNewTab) {
      const route = await snapshot(this._stateNav.link.brand$(path));
      const url = this._router.serializeUrl(this._router.createUrlTree(route));
      window.open(url, '_blank');
      return;
    }

    await this._stateNav.brand(path);
  }

  async create(
    brand: WithRef<IBrand>,
    referralData: InitReferralData,
    createdBy: StafferActor
  ): Promise<DocumentReference<IReferral> | undefined> {
    try {
      const ref = await Firestore.addDoc(
        Brand.referralCol(brand),
        Referral.init(referralData)
      );

      if (createdBy) {
        const referral = await Firestore.getDoc<IReferral>(ref);
        await this._interactionService.createReferralCreatedInteraction(
          referral,
          createdBy
        );
      }

      return ref;
    } catch {
      this._snackBar.open('Error creating referral');
      return;
    }
  }

  async update(
    referral: WithRef<IReferral>,
    data: Partial<IReferral>
  ): Promise<void> {
    const saveStartTime = this.startSaving();

    try {
      await Firestore.patchDoc(referral.ref, { ...data });
    } catch {
      this._snackBar.open('Error updating referral');
    } finally {
      await this.stopSaving(saveStartTime);
    }
  }

  async autoSave(
    referral: WithRef<IReferral>,
    changes: Partial<IReferral>,
    lastActivityBy: ReferralActor
  ): Promise<WithRef<IReferral> | undefined> {
    if (!changes || Object.keys(changes).length === 0) {
      return referral;
    }

    const saveStartTime = this.startSaving();

    try {
      const unreadBy = Referral.buildUnreadBy(referral, lastActivityBy);

      const statusChange = changes.status
        ? Referral.buildStatusChange(referral, changes.status)
        : {};

      const patch: Partial<IReferral> = {
        ...changes,
        ...statusChange,
        lastActivityBy,
        unreadBy,
      };

      if (statusChange.status) {
        await this._interactionService.createStatusChangedInteraction(
          referral,
          referral.status,
          statusChange.status,
          lastActivityBy
        );
      }

      await Firestore.patchDoc(referral.ref, patch);
      return { ...referral, ...patch };
    } catch {
      this._snackBar.open('Failed to save changes');
    } finally {
      await this.stopSaving(saveStartTime);
    }
  }

  async updateLastActivityBy(
    referral: WithRef<IReferral>,
    lastActivityBy: ReferralActor
  ): Promise<void> {
    try {
      const contact = Referral.getContact(referral);
      const actionIsByContact = isSameIdentifier(contact, lastActivityBy);
      const unreadBy = Referral.buildUnreadBy(referral, lastActivityBy);

      const update = {
        lastActivityBy,
        unreadBy,
        ...(actionIsByContact && { status: ReferralStatus.Received }),
      };

      await updateDoc(referral.ref, update);
    } catch {
      // eslint-disable-next-line no-console
      console.error('Failed to update last activity');
    }
  }

  async markAsRead(
    referral: WithRef<IReferral>,
    user: ReferralActor
  ): Promise<void> {
    try {
      const unreadBy = referral.unreadBy || [];

      if (!unreadBy.some((ref) => isSameIdentifier(ref, user.ref))) {
        return;
      }

      await Firestore.patchDoc(referral.ref, {
        unreadBy: unreadBy.filter((ref) => !isSameIdentifier(ref, user.ref)),
      });

      if (
        Referral.isParticipant(referral, user) ||
        Referral.isFollower(referral, user)
      ) {
        await this._interactionService.createReadByInteraction(referral, user);
      }
    } catch {
      this._snackBar.open('Error marking referral as read');
    }
  }

  async followReferral(
    referral: WithRef<IReferral>,
    staffer: StafferActor
  ): Promise<void> {
    try {
      const currentFollowers = referral.followers;
      if (currentFollowers.some((ref) => isSameIdentifier(ref, staffer))) {
        return;
      }
      const followers = [...currentFollowers, staffer.ref];
      await Firestore.patchDoc(referral.ref, { followers });
    } catch {
      this._snackBar.open('Error following referral');
    }
  }

  async unfollowReferral(
    referral: WithRef<IReferral>,
    staffer: StafferActor
  ): Promise<void> {
    try {
      const followers = referral.followers.filter(
        (ref) => !isSameIdentifier(ref, staffer)
      );
      await Firestore.patchDoc(referral.ref, { followers });
    } catch {
      this._snackBar.open('Error unfollowing referral');
    }
  }

  async delete(referral: WithRef<IReferral>): Promise<void> {
    try {
      const confirmed = await this._basicDialog.confirm(
        confirmationDialogData({
          title: 'Delete Referral',
          prompt: `Are you sure you want to delete this referral?`,
          submitLabel: 'Delete',
          submitColor: 'warn',
        })
      );

      if (!confirmed) {
        return;
      }
      await Firestore.deleteDoc(referral.ref);
      this._snackBar.open('Referral deleted');
    } catch {
      this._snackBar.open('Failed to delete referral');
      throw new Error('Failed to delete referral');
    }
  }

  startSaving(): number {
    this._isSaving.set(true);
    return Date.now();
  }

  async stopSaving(startTime: number): Promise<void> {
    const saveElapsedTime = Date.now() - startTime;
    const minimumSaveTime = 1000;

    if (saveElapsedTime < minimumSaveTime) {
      await new Promise((resolve) =>
        setTimeout(resolve, minimumSaveTime - saveElapsedTime)
      );
    }

    this._isSaving.set(false);
  }
}
